import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import toast from "react-hot-toast";
import Aos from "aos";
import { convertEnumToString, RefundStatus } from "../../lib/utils";
import { BookingStatus } from "../../types/booking";
import { all_routes } from "../router/all_routes";
import StatsCard from "../common/StatsCard";

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    bookingStore: {
      bookings,
      allBookings,
      setStatusFilter,
      loadBookings,
      loadAllBookings,
      clearBookings,
      pagination,
    },
    clientStore: { clientItems, loadClientItems,loadAllClients ,allClients,
},
    userStore,
    partnerStore: { partners, loadPendingPartners },
  } = useStore();

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });
  }, []);

  useEffect(() => {
    if (
      !userStore.isAdmin() &&
      !userStore.isCashier() &&
      !userStore.isAirportCashier()
    ) {
      clearBookings();
      navigate("/login");
      toast.error("Unauthorized");
    } else {
      setStatusFilter("");
      loadBookings();
      loadAllBookings();
      loadClientItems();
      loadPendingPartners();
      loadAllClients();
    }
  }, [userStore.token]);

  const pendingCount =
    bookings?.filter(
      (b) =>
        b.bookingStatus ===
        convertEnumToString(BookingStatus.Pending, BookingStatus)
    ).length || 0;

  const completedCount =
    bookings?.filter(
      (b) =>
        b.bookingStatus ===
        convertEnumToString(BookingStatus.Completed, BookingStatus)
    ).length || 0;

  const canceledCount =
    bookings?.filter(
      (b) =>
        b.bookingStatus ===
        convertEnumToString(BookingStatus.Canceled, BookingStatus)
    ).length || 0;

  const pendingPartnerCount = partners?.length || 0;

  const servicesWithDeposit = ["Tourist Visa", "Tourist Visa KSA"];

  let totalAdultCenterFee = 0;
  let totalChildCenterFee = 0;
  let totalSecurityDeposits = 0;

  const totalSales =
    allBookings?.reduce(
      (sum, b) => sum + parseFloat(String(b.totalPrice ?? "0")),
      0
    ) ?? 0;

  allBookings?.forEach((b) => {
  const adults = parseInt(String(b.adultsNumber ?? "0")) || 0;
  const children = parseInt(String(b.childrenNumber ?? "0")) || 0;
  const totalApplicants = adults + children;

  const regularPrice = parseFloat(String(b.servicePrice ?? "0"));
  const childPrice = parseFloat(String(b.childPrice ?? "0"));

  totalAdultCenterFee += regularPrice * adults;
  totalChildCenterFee += childPrice * children;

  // Case-insensitive match
  const hasDeposit = servicesWithDeposit.some((service) =>
    (b.serviceName ?? "").toLowerCase().includes(service.toLowerCase())
  );

  const customDeposit = parseFloat(String(b.customSecurityDeposit ?? "0")) || 0;
  const defaultDeposit = parseFloat(String(b.securityDeposit ?? "0")) || 0;
  const perPersonDeposit = customDeposit > 0 ? customDeposit : defaultDeposit;

  // ✅ Treat undefined as true (default behavior)
  const includeDeposit = b.includeSecurityDeposit !== false;

  // DEBUG


  if (hasDeposit && includeDeposit && perPersonDeposit > 0 && b.exception !== true) {
    const refundedCount =
      b.refundRequests?.filter((r) => r.status === RefundStatus.Approved).length ?? 0;

    const depositApplicants = totalApplicants - refundedCount;

    const securityDeposit =
      depositApplicants > 0 ? depositApplicants * perPersonDeposit : 0;

    totalSecurityDeposits += securityDeposit;
  }
  });

  const totalRefunds =
    allBookings
      ?.flatMap((b) => b.refundRequests || [])
      .reduce((sum, r) => sum + (r.requestedAmount ?? 0), 0) ?? 0;

  const totalProfit =
    totalSales -
    totalAdultCenterFee -
    totalChildCenterFee -
    totalSecurityDeposits;

const formattedProfit = `AED ${totalProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formattedRefunds = `AED ${totalRefunds.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formattedSales = `AED ${totalSales.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const stats = [];

  if (
    userStore.isCashier() ||
    userStore.isAdmin() ||
    userStore.isAirportCashier()
  ) {
    stats.push(
      {
        title: "All Bookings",
        count: pagination?.totalCount || 0,
        iconSrc: "/assets/img/uber.svg",
        iconBg: "bg-blue-100",
        linkTo: all_routes.bookingDashboard,
        onClick: () => setStatusFilter(""),
      },
      {
        title: "All Clients",
        count: allClients?.length || 0,
        iconSrc: "/assets/img/uber-08.svg",
        iconBg: "bg-indigo-100",
        linkTo: all_routes.clientDashboard,
        onClick: () => {},
      },
      {
        title: "Partners",
        count: pendingPartnerCount,
        iconSrc: "/assets/img/uberBook.png",
        iconBg: "bg-red-100",
        linkTo: all_routes.PartnerDashboard,
        onClick: () => {},
      }
    );
  }

  if (userStore.isAdmin()) {
    stats.push(
      {
        title: "Pending",
        count: pendingCount,
        iconSrc: "/assets/img/uberBook.png",
        iconBg: "bg-yellow-100",
        linkTo: all_routes.bookingDashboard,
        onClick: () => setStatusFilter("Pending"),
      },
      {
        title: "Completed",
        count: completedCount,
        iconSrc: "/assets/img/uberBook.png",
        iconBg: "bg-green-100",
        linkTo: all_routes.bookingDashboard,
        onClick: () => setStatusFilter("Completed"),
      },
      {
        title: "Canceled",
        count: canceledCount,
        iconSrc: "/assets/img/uberBook.png",
        iconBg: "bg-red-100",
        linkTo: all_routes.bookingDashboard,
        onClick: () => setStatusFilter("Canceled"),
      },
      {
        title: "Reports",
        count: allBookings?.length || 0,
        iconSrc: "/assets/img/uberBook.png",
        iconBg: "bg-red-100",
        linkTo: all_routes.reportDashboard,
        onClick: () => {},
      }
    );
  }

  return (
    
    <div className="bg-white-100 min-h-screen py-8 flex-grow pt-24 md:pt-32">
      <div className="max-w-screen-xl mx-auto px-4">
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">Dashboard</h2>

        {userStore.isAdmin() && (
          <div
            className="mt-10 mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-aos="fade-up"
          >
            <div className="bg-gradient-to-r from-green-100 to-green-50 hover:shadow-xl transition-shadow duration-300 rounded-xl shadow p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Total Sales
                </h3>
                <p className="text-2xl font-extrabold text-green-700">
                  {formattedSales}
                </p>
              </div>
              <div className="hidden sm:block transform transition-transform duration-500 hover:scale-110">
                <img
                  src="/assets/img/sales.png"
                  alt="Sales Icon"
                  className="h-20 w-20"
                />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-purple-50 hover:shadow-xl transition-shadow duration-300 rounded-xl shadow p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Total Profit
                </h3>
                <p className="text-2xl font-extrabold text-purple-700">
                  {formattedProfit}
                </p>
              </div>
              <div className="hidden sm:block transform transition-transform duration-500 hover:scale-110">
                <img
                  src="/assets/img/profit.png"
                  alt="Profit Icon"
                  className="h-20 w-20"
                />
              </div>
            </div>
            <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 hover:shadow-xl transition-shadow duration-300 rounded-xl shadow p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {" "}
                  Deposits in Hand
                </h3>
                <p className="text-2xl font-extrabold text-yellow-700">{`AED ${totalSecurityDeposits.toFixed(2)}`}</p>
              </div>
              <div className="hidden sm:block transform transition-transform duration-500 hover:scale-110">
                <img
                  src="/assets/img/refund.svg"
                  alt="Security Deposit Icon"
                  className="h-20 w-20"
                />
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-100 to-red-50 hover:shadow-xl transition-shadow duration-300 rounded-xl shadow p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Total Refunds
                </h3>
                <p className="text-2xl font-extrabold text-red-600">
                  {formattedRefunds}
                </p>
              </div>
              <div className="hidden sm:block transform transition-transform duration-500 hover:scale-110">
                <img
                  src="/assets/img/refund.svg"
                  alt="Refund Icon"
                  className="h-20 w-20"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <StatsCard
              key={idx}
              title={stat.title}
              count={stat.count}
              iconSrc={stat.iconSrc}
              iconBg={stat.iconBg}
              linkTo={stat.linkTo}
              onClick={stat.onClick}
            />
          ))}
        </div>

        {/* Top Customers Table */}
        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4">Customers</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto text-sm text-left text-gray-600">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 font-semibold">#</th>
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-4 py-2 font-semibold">Phone</th>
                  <th className="px-4 py-2 font-semibold">No. of Sales</th>
                </tr>
              </thead>
              <tbody>
                {allBookings &&
                  Object.values(
                    allBookings.reduce(
                      (acc, booking) => {
                        const key = booking.email || booking.phone;
                        if (!key) return acc;
                        if (!acc[key]) {
                          acc[key] = {
                            email: booking.email || "N/A",
                            phone: booking.phone || "N/A",
                            salesCount: 1,
                          };
                        } else {
                          acc[key].salesCount += 1;
                        }
                        return acc;
                      },
                      {} as Record<
                        string,
                        { email: string; phone: string; salesCount: number }
                      >
                    )
                  )
                    .sort((a, b) => b.salesCount - a.salesCount)
                    .slice(0, 10)
                    .map((client, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">{index + 1}</td>
                        <td className="px-4 py-2">{client.email}</td>
                        <td className="px-4 py-2">{client.phone}</td>
                        <td className="px-4 py-2 font-bold text-blue-600">
                          {client.salesCount}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
          {allBookings && allBookings.length > 10 && (
            <p className="mt-2 text-sm text-gray-500">
              Showing top 10 customers by sales (out of {allBookings.length})
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(Dashboard);
